import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProducts, getCategoriesHierarchical } from '../../utils/api';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useQuickView } from '../../context/QuickViewContext';
import { getImageUrl, getUnsplashFallback } from '../../utils/imageUtils';
import './Home.css';

/* ─── helper: (re)initialise a single Swiper after React renders ─── */
const initSwiper = (selector, config) => {
  if (!window.Swiper) return null;
  try {
    const el = document.querySelector(selector);
    if (!el) return null;
    if (el.swiper) el.swiper.destroy(true, true);
    return new window.Swiper(el, config);
  } catch (e) { return null; }
};

/* ─── fallback images for category banners ─── */
const FALLBACK_CAT_IMAGES = [
  '/ultras/images/collection-item1.jpg',
  '/ultras/images/collection-item2.jpg',
  '/ultras/images/collection-item3.jpg',
  '/ultras/images/model.jpg',
  '/ultras/images/single-image1.jpg',
  '/ultras/images/single-image2.jpg',
  '/ultras/images/banner1.jpg',
  '/ultras/images/banner2.jpg',
];

/* default collections used to pad the grid to a full Uniworth-style layout */
const DEFAULT_COLLECTIONS = [
  'New Arrivals', 'Best Sellers', 'Trending Now', 'Top Rated',
  'Featured', 'On Sale', 'Premium', 'Clearance',
];

/* ─── inline feature SVGs (match Uniworth) ─── */
const TruckIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="18" width="34" height="26" rx="2" />
    <path d="M37 25h11l8 9v10H37z" />
    <circle cx="17" cy="48" r="5" /><circle cx="46" cy="48" r="5" />
    <path d="M8 28h14M8 34h10" />
    <text x="11" y="15" fontSize="8" fontWeight="700" stroke="none" fill="currentColor">FREE</text>
  </svg>
);
const CardIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="14" width="44" height="30" rx="3" />
    <path d="M6 23h44" />
    <path d="M12 35h12" />
    <path d="M46 36l6 3 8-4v8c0 6-5 9-8 10-3-1-8-4-8-10v-7z" fill="none" />
    <path d="M50 46l2 2 4-4" />
  </svg>
);
const BadgeIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M32 6l5 4 6-1 2 6 5 3-2 6 2 6-5 3-2 6-6-1-5 4-5-4-6 1-2-6-5-3 2-6-2-6 5-3 2-6 6 1z" />
    <path d="M24 30l3 6 5-9 5 9 3-6v8H24z" />
  </svg>
);

/* ─── Video slides for the bottom slider ───
   Add your own video file paths here (drop the files in /public/assets/videos/
   or anywhere under /public and reference them with a leading slash).
   Each entry becomes one slide. The poster image shows until the video loads. */
const VIDEO_SLIDES = [
  // Add your video file(s) here later, e.g.:
  // { src: '/assets/videos/your-video-1.mp4', poster: '/ultras/images/banner1.jpg' },
  // { src: '/assets/videos/your-video-2.mp4', poster: '/ultras/images/banner2.jpg' },
];

const Home = () => {
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();
  const { showQuickView } = useQuickView();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [nbtTab, setNbtTab] = useState('new');
  const [showDiscount, setShowDiscount] = useState(false);
  const nbtSwiperRef = useRef(null);

  /* ── fetch data ── */
  useEffect(() => {
    Promise.all([getProducts(), getCategoriesHierarchical()])
      .then(([prods, cats]) => {
        const mapped = (prods || []).slice(0, 24).map((p, i) => {
          let imgs = [];
          try { imgs = typeof p.imagePaths === 'string' ? JSON.parse(p.imagePaths) : (p.imagePaths || []); } catch { imgs = []; }
          return {
            id: p.id,
            name: p.productName || p.name || 'Product',
            category: p.category?.categoryName || 'General',
            categoryId: p.category?.id || null,
            price: Math.round(p.price || 0),
            image: getImageUrl(imgs[0] || p.imagePath) || getUnsplashFallback(i),
          };
        });
        setProducts(mapped);
        setCategories((cats || []).filter(c => c.isActive));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* ── build category banners (real cats first, padded with defaults) ── */
  const catBanners = (() => {
    const real = categories.map((c, i) => ({
      id: c.id,
      name: c.categoryName,
      img: getImageUrl(c.imagePath) || FALLBACK_CAT_IMAGES[i % FALLBACK_CAT_IMAGES.length],
      link: `/shop?categoryId=${c.id}`,
    }));
    const padded = [...real];
    let d = 0;
    while (padded.length < 8 && d < DEFAULT_COLLECTIONS.length) {
      padded.push({
        id: `def-${d}`,
        name: DEFAULT_COLLECTIONS[d],
        img: FALLBACK_CAT_IMAGES[(real.length + d) % FALLBACK_CAT_IMAGES.length],
        link: '/shop',
      });
      d++;
    }
    return padded.slice(0, 8);
  })();
  const row1 = catBanners.slice(0, 2);
  const row2 = catBanners.slice(2, 5);
  const row3 = catBanners.slice(5, 8);

  /* ── NBT product subsets ── */
  const nbtProducts =
    nbtTab === 'new' ? products.slice(0, 10)
      : nbtTab === 'best' ? products.slice(4, 14)
        : products.slice(8, 18);

  /* ── init hero + featured + flash swipers once products load ── */
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      initSwiper('.main-swiper', {
        speed: 600, loop: true,
        autoplay: { delay: 4000, disableOnInteraction: false },
        navigation: { nextEl: '#billboard .button-next', prevEl: '#billboard .button-prev' },
        pagination: { el: '#billboard .swiper-pagination', clickable: true },
      });
      initSwiper('#featured-products .product-swiper', {
        loop: true, autoplay: { delay: 3000, disableOnInteraction: false },
        pagination: { el: '#featured-products .swiper-pagination', clickable: true },
        breakpoints: { 0: { slidesPerView: 1, spaceBetween: 24 }, 576: { slidesPerView: 2, spaceBetween: 24 }, 992: { slidesPerView: 3, spaceBetween: 24 }, 1200: { slidesPerView: 4, spaceBetween: 24 } },
      });
      initSwiper('.lookbook-swiper', {
        loop: VIDEO_SLIDES.length > 1, spaceBetween: 0,
        autoplay: VIDEO_SLIDES.length > 1 ? { delay: 4000, disableOnInteraction: false } : false,
        navigation: { nextEl: '.lb-next', prevEl: '.lb-prev' },
        pagination: { el: '.lookbook-swiper .swiper-pagination', clickable: true },
        slidesPerView: 1,
      });
    }, 200);
    return () => clearTimeout(t);
  }, [loading]);

  /* ── (re)init NBT slider whenever tab/products change ── */
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => {
      nbtSwiperRef.current = initSwiper('.nbt-swiper', {
        loop: true, autoplay: { delay: 2800, disableOnInteraction: false },
        navigation: { nextEl: '.nbt-next', prevEl: '.nbt-prev' },
        breakpoints: { 0: { slidesPerView: 1, spaceBetween: 20 }, 576: { slidesPerView: 2, spaceBetween: 20 }, 992: { slidesPerView: 3, spaceBetween: 24 }, 1200: { slidesPerView: 4, spaceBetween: 24 } },
      });
    }, 150);
    return () => clearTimeout(t);
  }, [loading, nbtTab, products.length]);

  const handleAddToCart = (p) => {
    addToCart({ id: p.id, name: p.name, productName: p.name, price: p.price, image: p.image, quantity: 1, stock: 999 });
  };

  return (
    <main>

      {/* ═══════════ HERO BILLBOARD ═══════════ */}
      <section id="billboard" className="overflow-hidden">
        <button className="button-prev"><i className="icon icon-chevron-left"></i></button>
        <button className="button-next"><i className="icon icon-chevron-right"></i></button>
        <div className="swiper main-swiper">
          <div className="swiper-wrapper">
            <div className="swiper-slide" style={{ backgroundImage: "url('/ultras/images/banner1.jpg')", backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="banner-content">
                <div className="container"><div className="row"><div className="col-md-6">
                  <h2 className="banner-title">New Collection</h2>
                  <p>Explore our latest arrivals — quality products at wholesale prices for retailers and online sellers.</p>
                  <div className="btn-wrap"><Link to="/shop" className="btn btn-light btn-medium d-flex align-items-center">Shop it now <i className="icon icon-arrow-io"></i></Link></div>
                </div></div></div>
              </div>
            </div>
            <div className="swiper-slide" style={{ backgroundImage: "url('/ultras/images/banner2.jpg')", backgroundRepeat: 'no-repeat', backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="banner-content">
                <div className="container"><div className="row"><div className="col-md-6">
                  <h2 className="banner-title">Best Deals</h2>
                  <p>Competitive pricing on bulk orders. Perfect for resellers, retailers, and growing businesses.</p>
                  <div className="btn-wrap"><Link to="/shop" className="btn btn-light btn-light-arrow btn-medium d-flex align-items-center">Shop it now <i className="icon icon-arrow-io"></i></Link></div>
                </div></div></div>
              </div>
            </div>
          </div>
          <div className="swiper-pagination"></div>
        </div>
      </section>

      {/* ═══════════ CATEGORY ROW 1 (2-up) ═══════════ */}
      <section className="cat-grid cat-grid-2">
        <div className="container">
          <div className="row">
            {row1.map(cat => (
              <div className="col-md-6" key={cat.id}>
                <Link to={cat.link} className="cat-banner">
                  <img src={cat.img} alt={cat.name} onError={e => { e.target.src = FALLBACK_CAT_IMAGES[0]; }} />
                  <span className="cat-overlay">
                    <span className="cat-shop">Shop</span>
                    <span className="cat-name">{cat.name}</span>
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ CATEGORY ROW 2 (3-up) ═══════════ */}
      <section className="cat-grid cat-grid-3">
        <div className="container">
          <div className="row">
            {row2.map(cat => (
              <div className="col-md-4" key={cat.id}>
                <Link to={cat.link} className="cat-banner">
                  <img src={cat.img} alt={cat.name} onError={e => { e.target.src = FALLBACK_CAT_IMAGES[1]; }} />
                  <span className="cat-overlay">
                    <span className="cat-shop">Shop</span>
                    <span className="cat-name">{cat.name}</span>
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ NEW / BEST SELLER / TRENDING (auto slider) ═══════════ */}
      <section className="nbt-section">
        <div className="container">
          <ul className="nbt-tabs">
            <li className={`nbt-tab${nbtTab === 'new' ? ' active' : ''}`} onClick={() => setNbtTab('new')}>New</li>
            <li className="nbt-sep">/</li>
            <li className={`nbt-tab${nbtTab === 'best' ? ' active' : ''}`} onClick={() => setNbtTab('best')}>Best Seller</li>
            <li className="nbt-sep">/</li>
            <li className={`nbt-tab${nbtTab === 'trending' ? ' active' : ''}`} onClick={() => setNbtTab('trending')}>Trending</li>
          </ul>

          <div className="nbt-swiper-wrap">
            <button className="nbt-arrow nbt-prev" aria-label="Previous"><i className="icon icon-chevron-left"></i></button>
            <button className="nbt-arrow nbt-next" aria-label="Next"><i className="icon icon-chevron-right"></i></button>
            <div className="swiper nbt-swiper overflow-hidden" key={nbtTab}>
              <div className="swiper-wrapper">
                {(loading ? [] : nbtProducts).map((p, i) => (
                  <div className="swiper-slide" key={p.id}>
                    <div className="nbt-card">
                      <div className="nbt-img">
                        <Link to={`/shop-product-right?id=${p.id}`}>
                          <img src={p.image} alt={p.name} onError={e => { e.target.src = getUnsplashFallback(i); }} />
                        </Link>
                        <div className="nbt-actions">
                          <button type="button" aria-label="Add to cart" onClick={() => handleAddToCart(p)}><i className="icon icon-shopping-cart"></i></button>
                          <button type="button" aria-label="Wishlist" onClick={() => addToWishlist(p)}><i className="icon icon-heart" style={isInWishlist(p.id) ? { color: 'red' } : {}}></i></button>
                          <button type="button" aria-label="Quick view" onClick={() => showQuickView(p)}><i className="icon icon-screen-full"></i></button>
                        </div>
                      </div>
                      <h3 className="nbt-title"><Link to={`/shop-product-right?id=${p.id}`}>{p.name}</Link></h3>
                      <div className="nbt-price">${p.price.toFixed(2)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ CATEGORY ROW 3 (3-up) ═══════════ */}
      <section className="cat-grid cat-grid-3">
        <div className="container">
          <div className="row">
            {row3.map(cat => (
              <div className="col-md-4" key={cat.id}>
                <Link to={cat.link} className="cat-banner">
                  <img src={cat.img} alt={cat.name} onError={e => { e.target.src = FALLBACK_CAT_IMAGES[2]; }} />
                  <span className="cat-overlay">
                    <span className="cat-shop">Shop</span>
                    <span className="cat-name">{cat.name}</span>
                  </span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURE ICONS ═══════════ */}
      <section className="feature-icons">
        <div className="container">
          <div className="row">
            <div className="col-md-4">
              <div className="feature-box">
                <span className="f-icon"><TruckIcon /></span>
                <h4>Free Shipping</h4>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-box">
                <span className="f-icon"><CardIcon /></span>
                <h4>Secure Payments</h4>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-box">
                <span className="f-icon"><BadgeIcon /></span>
                <h4>Premium Quality</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FEATURED PRODUCTS (existing swiper, kept) ═══════════ */}
      <section id="featured-products" className="product-store padding-large">
        <div className="container">
          <div className="section-header d-flex flex-wrap align-items-center justify-content-between">
            <h2 className="section-title">Featured Products</h2>
            <div className="btn-wrap"><Link to="/shop" className="d-flex align-items-center">View all products <i className="icon icon-arrow-io"></i></Link></div>
          </div>
          <div className="swiper product-swiper overflow-hidden">
            <div className="swiper-wrapper">
              {(loading ? [] : products.slice(0, 8)).map((p, i) => (
                <div key={p.id} className="swiper-slide">
                  <div className="product-item">
                    <div className="image-holder">
                      <img src={p.image} alt={p.name} className="product-image" onError={e => { e.target.src = getUnsplashFallback(i); }} />
                    </div>
                    <div className="cart-concern">
                      <div className="cart-button d-flex justify-content-between align-items-center">
                        <button type="button" className="btn-wrap cart-link d-flex align-items-center" onClick={() => handleAddToCart(p)}>add to cart <i className="icon icon-arrow-io"></i></button>
                        <button type="button" className="view-btn tooltip d-flex" onClick={() => showQuickView(p)}><i className="icon icon-screen-full"></i><span className="tooltip-text">Quick view</span></button>
                        <button type="button" className="wishlist-btn" onClick={() => addToWishlist(p)}><i className="icon icon-heart" style={isInWishlist(p.id) ? { color: 'red' } : {}}></i></button>
                      </div>
                    </div>
                    <div className="product-detail">
                      <h3 className="product-title"><Link to={`/shop-product-right?id=${p.id}`}>{p.name}</Link></h3>
                      <span className="item-price text-primary">${p.price.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="swiper-pagination"></div>
        </div>
      </section>

      {/* ═══════════ INSTAGRAM / DISCOUNT ═══════════ */}
      <section className="lookbook">
        <div className="container">
          <div className="lookbook-head">
            <h2>@aanduwholeseller</h2>
            <div className="discount-wrap">
              <button className="discount-btn" onClick={() => setShowDiscount(v => !v)} aria-label="Get discount">+</button>
              <span className="discount-pill">Get 10% Off</span>
              {showDiscount && (
                <div className="discount-popup">
                  <h5>🎉 Get 10% Off Your First Order!</h5>
                  <p>Sign up to our newsletter and unlock an instant 10% discount.</p>
                  <p>Free shipping on every order over $200.</p>
                  <p>New customers only · valid for 7 days.</p>
                  <span className="code">WELCOME10</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="insta-gallery">
          {[1, 2, 3, 4, 5].map(n => (
            <div className="insta-cell" style={{ flex: '1 1 20%' }} key={n}>
              <img src={`/ultras/images/insta-image${n}.jpg`} alt={`gallery ${n}`} onError={e => { e.target.src = FALLBACK_CAT_IMAGES[n % FALLBACK_CAT_IMAGES.length]; }} />
              <span className="insta-icon"><i className="icon icon-instagram"></i></span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════ VIDEO SLIDER (full width) — shows only when videos are added ═══════════ */}
      {VIDEO_SLIDES.length > 0 && (
      <section className="video-strip">
        <div className="lookbook-wrap">
          <button className="lb-arrow lb-prev" aria-label="Previous"><i className="icon icon-chevron-left"></i></button>
          <button className="lb-arrow lb-next" aria-label="Next"><i className="icon icon-chevron-right"></i></button>
          <div className="swiper lookbook-swiper overflow-hidden">
            <div className="swiper-wrapper">
              {VIDEO_SLIDES.map((v, i) => (
                <div className="swiper-slide" key={`vid-${i}`}>
                  <div className="vs-cell">
                    <video
                      src={v.src}
                      poster={v.poster}
                      muted
                      loop
                      autoPlay
                      playsInline
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="swiper-pagination"></div>
          </div>
        </div>
      </section>
      )}

    </main>
  );
};

export default Home;
